// Demo Readiness Panel — Done page surface for the PR 5 polisher.
//
// Reads from POST /api/org/polish (with 5-min cache) and renders:
//   - the top-level verdict pill (green / amber / red)
//   - per-check rows with severity dot + title + detail
//   - "Re-polish" button that bypasses the cache
//   - per-red row "Fix now" affordance (Week 3 — UI hook only;
//     route layer dispatch is implemented in the same PR)
//
// Soft-load: when the route returns null (no auth, /polish 404 in
// dev), the panel quietly hides itself rather than rendering a
// confusing empty card. This matches the existing api.preflight
// pattern.

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

interface PolishCheck {
  name: string;
  severity: "green" | "amber" | "red";
  title: string;
  detail: string;
  runbook?: string | null;
  fix_action?: string | null;
  metrics?: Record<string, unknown>;
}

interface PolishPayload {
  severity: "green" | "amber" | "red";
  message: string;
  checks: PolishCheck[];
  ran_at: string;
  cached: boolean;
  cache_ttl_seconds: number;
}

const _SEVERITY_COLORS = {
  green: { bg: "rgba(76,175,80,.08)", border: "rgba(76,175,80,.4)", fg: "#1b5e20", dot: "#4caf50" },
  amber: { bg: "rgba(245,166,35,.08)", border: "rgba(245,166,35,.45)", fg: "#5d3b00", dot: "#f5a623" },
  red:   { bg: "rgba(239,83,80,.06)",  border: "rgba(239,83,80,.4)",  fg: "#b71c1c", dot: "#e53935" },
} as const;


export function DemoReadinessPanel(): JSX.Element | null {
  const qc = useQueryClient();
  const [forceFresh, setForceFresh] = useState(false);

  const query = useQuery<PolishPayload | null>({
    queryKey: ["polish-verdict", forceFresh],
    queryFn: () => (api as { polishOrg?: (useCache: boolean) => Promise<PolishPayload | null> })
      .polishOrg?.(!forceFresh) ?? Promise.resolve(null),
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  const repolishMut = useMutation({
    mutationFn: () =>
      (api as { polishOrg?: (useCache: boolean) => Promise<PolishPayload | null> })
        .polishOrg?.(false) ?? Promise.resolve(null),
    onSuccess: (data) => {
      qc.setQueryData(["polish-verdict", false], data);
      qc.setQueryData(["polish-verdict", true], data);
      setForceFresh(false);
    },
  });

  if (query.isLoading) {
    return (
      <div style={{ marginTop: 32, fontSize: 12, color: "var(--ink-3)" }}>
        Running demo readiness checks…
      </div>
    );
  }

  const payload = query.data;
  if (!payload) {
    // Route returned null (unauthenticated dev / 404 / network) —
    // hide the panel rather than render a confusing empty card.
    return null;
  }

  const palette = _SEVERITY_COLORS[payload.severity];
  const ranAtLabel = _formatAge(payload.ran_at);

  return (
    <div
      style={{
        marginTop: 32,
        textAlign: "left",
        padding: 16,
        borderRadius: 12,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          marginBottom: 8,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: 12,
            height: 12,
            borderRadius: 999,
            background: palette.dot,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: palette.fg }}>
            Demo readiness · {payload.severity.toUpperCase()}
          </div>
          <div style={{ fontSize: 11, color: "var(--ink-2)", marginTop: 2 }}>
            {payload.message} · last polish {ranAtLabel}
            {payload.cached ? " (cached)" : ""}
          </div>
        </div>
        <button
          type="button"
          className="btn ghost sm"
          disabled={repolishMut.isPending}
          onClick={() => {
            setForceFresh(true);
            repolishMut.mutate();
          }}
        >
          {repolishMut.isPending ? "Re-polishing…" : "Re-polish"}
        </button>
      </div>

      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
          display: "grid",
          gap: 6,
        }}
      >
        {payload.checks.map((check) => (
          <CheckRow key={check.name} check={check} />
        ))}
      </ul>
    </div>
  );
}


function CheckRow({ check }: { check: PolishCheck }): JSX.Element {
  const palette = _SEVERITY_COLORS[check.severity];
  const isRed = check.severity === "red";
  const fixHref = check.runbook
    ? (check.runbook.startsWith("http")
        ? check.runbook
        : `/${check.runbook.replace(/^\/+/, "")}`)
    : null;

  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
        padding: "8px 10px",
        borderRadius: 8,
        background: "white",
        border: `1px solid ${palette.border}`,
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 10,
          height: 10,
          borderRadius: 999,
          background: palette.dot,
          marginTop: 5,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>
          {check.title}
        </div>
        <div style={{ fontSize: 11, color: "var(--ink-2)", marginTop: 2 }}>
          {check.detail}
        </div>
      </div>
      {isRed && fixHref && (
        <a
          href={fixHref}
          target="_blank"
          rel="noreferrer"
          className="btn ghost sm"
          style={{ flexShrink: 0 }}
          title={check.runbook || undefined}
        >
          Fix now ↗
        </a>
      )}
    </li>
  );
}


function _formatAge(iso: string): string {
  try {
    const ts = new Date(iso);
    const ms = Date.now() - ts.getTime();
    if (ms < 60_000) return "just now";
    const minutes = Math.floor(ms / 60_000);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  } catch {
    return iso;
  }
}
